import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { clsx } from 'clsx';
import { SimpleSwitch } from '@/components/ui';
import { UserGroupService } from '@/services/userGroups.service';
import { useNotificationStore } from '@/store/notificationStore';
import { UserGroupRightsDialog } from './UserGroupRightsDialog';
import type { GroupRole, UserGroup } from '@/types/userGroups.types';

interface UserGroupRightsItemProps {
  groupRole: GroupRole;
  userGroups: UserGroup[];
  onUpdateUserGroupRoles: (roleName: string) => void;
}

/**
 * UserGroupRightsItem - Component showing a single role and its permissions across user groups
 * Now with Switch components similar to the Angular implementation
 */
export const UserGroupRightsItem: React.FC<UserGroupRightsItemProps> = ({
  groupRole,
  userGroups,
  onUpdateUserGroupRoles,
}) => {
  const { addNotification } = useNotificationStore();
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const [isAdvancedEditOpen, setIsAdvancedEditOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    if (openMenuId !== null && menuButtonRef.current) {
      const rect = menuButtonRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom, // fixed is viewport-relative — no scrollY offset needed
        left: rect.right - 192, // 192px = w-48
      });
    } else {
      setMenuPosition(null);
    }
  }, [openMenuId]);

  // Local state for role toggles (optimistic updates like Angular)
  const [localRoleStates, setLocalRoleStates] = useState<Record<number, boolean>>({});

  // Safety check: if groupRole or userGroups is invalid, return null
  if (!groupRole || !userGroups || !Array.isArray(userGroups)) {
    return null;
  }

  // Check if a user group has this role (check local state first, then props)
  const hasRole = (userGroup: UserGroup): boolean => {
    if (!userGroup || !groupRole) return false;

    // Check if we have local state for this user group
    if (Object.prototype.hasOwnProperty.call(localRoleStates, userGroup.id)) {
      return localRoleStates[userGroup.id];
    }

    // Fall back to props
    if (!userGroup.roleCodes) return false;
    return userGroup.roleCodes.includes(groupRole.roleCode);
  };

  // Check if role is required for user group
  const isRequiredRole = (userGroup: UserGroup): boolean => {
    if (!userGroup || !userGroup.requiredRoleCodes || !groupRole) return false;
    return userGroup.requiredRoleCodes.includes(groupRole.roleCode) || false;
  };

  // Handle toggle role for a user group
  const handleToggleRole = async (userGroup: UserGroup, newValue: boolean) => {
    // Optimistically update local state immediately (Angular behavior)
    setLocalRoleStates((prev) => ({
      ...prev,
      [userGroup.id]: newValue,
    }));

    try {
      await UserGroupService.updateGroupRoleStatus(groupRole.roleCode, userGroup.id, newValue);

      // Success - no notification needed for simple toggle (cleaner UX)
      // Local state is already updated optimistically
    } catch (error) {
      // Revert local state on error (Angular behavior)
      setLocalRoleStates((prev) => ({
        ...prev,
        [userGroup.id]: !newValue,
      }));

      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      addNotification({
        type: 'error',
        title: 'Role Update Failed',
        message: errorMessage,
      });
    }
  };

  const toggleMenu = (event: React.MouseEvent) => {
    event.stopPropagation();
    setOpenMenuId(openMenuId ? null : groupRole.roleId);
  };

  const handleAdvancedEdit = () => {
    setOpenMenuId(null);
    setIsAdvancedEditOpen(true);
  };

  const handleAdvancedEditSuccess = () => {
    onUpdateUserGroupRoles(groupRole.roleName);
  };

  return (
    <>
      <div className="group border-b border-gray-200 bg-white hover:bg-gray-50 dark:border-dark-600 dark:bg-dark-900 dark:hover:bg-dark-800">
        <div className="flex flex-col md:flex-row md:items-center">
          {/* Role Information Column - Sticky Left */}
          <div className="z-10 flex w-full items-center justify-between bg-white px-4 py-3 group-hover:bg-gray-50 dark:bg-dark-900 dark:group-hover:bg-dark-800 md:sticky md:left-0 md:w-[280px] md:min-w-[280px] md:max-w-[280px]">
            <div className="min-w-0 flex-1 pr-2">
              <div className="truncate text-sm font-medium text-gray-900 dark:text-white">
                {groupRole.roleName}
              </div>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={toggleMenu}
              className="flex-shrink-0 rounded p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-600 dark:hover:bg-dark-700 dark:hover:text-gray-300 md:hidden"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                />
              </svg>
            </button>
          </div>

          {/* User Group Permission Switches */}
          <div className="flex flex-1 flex-wrap items-start gap-3 px-4 py-3 md:flex-nowrap md:items-center md:gap-4 md:py-0">
            {userGroups.filter(Boolean).map((userGroup) => {
              if (!userGroup || !userGroup.id) return null;

              const isActive = hasRole(userGroup);
              const isRequired = isRequiredRole(userGroup);
              const isDisabled = !userGroup.isActive || isRequired;

              return (
                <div
                  key={userGroup.id}
                  className={clsx(
                    'w-full flex-shrink-0 md:w-[140px]',
                    !userGroup.isActive && 'opacity-50'
                  )}
                >
                  <div className="mb-1 text-center text-[11px] font-medium text-gray-500 dark:text-gray-400 md:hidden">
                    {userGroup.name}
                  </div>

                  <div className="flex items-center justify-center">
                    {isRequired ? (
                      <div
                        className="flex h-6 w-11 items-center justify-center rounded-full bg-gray-300 dark:bg-dark-600"
                        title={`Required for ${userGroup.name}`}
                      >
                        <svg
                          className="h-4 w-4 text-gray-500 dark:text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                          />
                        </svg>
                      </div>
                    ) : (
                      <SimpleSwitch
                        checked={isActive}
                        onChange={(checked) => handleToggleRole(userGroup, checked)}
                        disabled={isDisabled}
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Desktop Actions Column - Sticky Right */}
          <div className="sticky right-0 z-10 hidden w-20 flex-shrink-0 items-center justify-center bg-white group-hover:bg-gray-50 dark:bg-dark-900 dark:group-hover:bg-dark-800 md:flex">
            <button
              ref={menuButtonRef}
              onClick={toggleMenu}
              className="rounded p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-600 dark:hover:bg-dark-700 dark:hover:text-gray-300"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                />
              </svg>
            </button>
          </div>

          {/* Dropdown Menu — portalled to document.body to escape all stacking contexts */}
          {openMenuId === groupRole.roleId &&
            menuPosition &&
            ReactDOM.createPortal(
              <>
                <div className="fixed inset-0 z-[100]" onClick={() => setOpenMenuId(null)} />
                <div
                  className="fixed z-[101] w-48 rounded-md border border-gray-200 bg-white shadow-lg dark:border-dark-600 dark:bg-dark-700"
                  style={{ top: `${menuPosition.top}px`, left: `${menuPosition.left}px` }}
                >
                  <div className="py-1">
                    <button
                      onClick={handleAdvancedEdit}
                      className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-dark-600"
                    >
                      <svg
                        className="h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                      Advanced Edit
                    </button>
                  </div>
                </div>
              </>,
              document.body
            )}
        </div>
      </div>

      {/* Advanced Edit Dialog */}
      <UserGroupRightsDialog
        isOpen={isAdvancedEditOpen}
        onClose={() => setIsAdvancedEditOpen(false)}
        groupRole={groupRole}
        userGroups={userGroups}
        onSuccess={handleAdvancedEditSuccess}
      />
    </>
  );
};
